import Tippy from '@tippyjs/react'
import { type ReactNode, useEffect, useState } from 'react'
import { classNameParser } from '../../../../helpers'
import type { InputProps } from '../../../../types'

import '../../../../styles/input.css'
import './style.css'

import helpImage from '../../../../images/ic-help.svg'

type PhoneInputProps = InputProps<
  string,
  {
    fieldInfoSize?: 'small' | 'large'
    fieldInfo?: ReactNode
    hardFillValue?: string
  }
>

const normalizeInput = (newValue: string): string => {
  // return nothing if no value
  if (!newValue) return newValue

  // only allows 0-9 inputs
  const currentValue = newValue.replace(/[^\d]/g, '')

  // returns: "x", "xx", "xxx"
  if (currentValue.length < 4) return currentValue

  // returns: "(xxx)", "(xxx) x", "(xxx) xx", "(xxx) xxx",
  if (currentValue.length <= 6) return `${currentValue.slice(0, 3)}-${currentValue.slice(3)}`

  // returns: "(xxx) xxx-", (xxx) xxx-x", "(xxx) xxx-xx", "(xxx) xxx-xxx", "(xxx) xxx-xxxx"
  return `${currentValue.slice(0, 3)}-${currentValue.slice(3, 6)}-${currentValue.slice(6, 10)}`
}

export const PhoneInput = (props: PhoneInputProps) => {
  const [value, setValue] = useState<string>(normalizeInput(props.value ?? ''))
  const [infoTarget, setInfoTarget] = useState<HTMLElement>()
  const [fieldTarget, setFieldTarget] = useState<HTMLElement>()

  useEffect(() => {
    if (props.hardFillValue !== undefined) {
      setValue(normalizeInput(props.hardFillValue))
    }
  }, [props.hardFillValue])

  return (
    <>
      <Tippy
        reference={fieldTarget}
        triggerTarget={infoTarget}
        disabled={!props.fieldInfo}
        content={
          <div className="global--tooltip-content bg-white w-full rounded-md shadow-2xl px-[16px] py-[4px]">
            {props.fieldInfo}
          </div>
        }
      />
      <div
        ref={(el) => {
          if (el) setFieldTarget(el)
        }}
        className={classNameParser('global--input-container input--phone-input-container', props.className)}
        id={props.id}
        key={props.key}
        style={props.style}
      >
        <div className="absolute right-0 top-[-20px] flex items-center">
          {props.optional === true ? (
            <p className={`global--input-label font-400 !text-[12px]`}>Optional field</p>
          ) : null}
        </div>
        <input
          type="tel"
          pattern={'^([0-9]{3} |[0-9]{3}-)[0-9]{3}-[0-9]{4}$'}
          className={`global--input ${value ? 'global--with-value' : ''} ${
            props.fieldInfo !== undefined ? '!pr-[35px]' : ''
          } ${!props.autoComplete ? 'hide-autocomplete' : ''}`}
          onChange={(event) => {
            const phoneNumberFormat = normalizeInput(event.target.value)
            setValue(phoneNumberFormat)
            props.onChange && props.onChange(phoneNumberFormat)
          }}
          value={value}
          disabled={props.disabled}
          // biome-ignore lint/a11y/noAutofocus: opt-in through the autoFocus prop
          autoFocus={props.autoFocus}
        />
        {props.fieldInfo !== undefined ? (
          <div
            ref={(el) => {
              if (el) setInfoTarget(el)
            }}
            className="absolute right-0 top-0"
          >
            <img src={helpImage.src} alt="help icon" width={48} height={48}></img>
          </div>
        ) : null}
        {props.label && (
          <label htmlFor={props.id} className="input--phone-input-label" title={props.label}>
            <span className="input--phone-input-label-text">{props.label}</span>
          </label>
        )}
        {props.error && <p className="global--input-error">{props.error}</p>}
      </div>
    </>
  )
}
